import styled from "@emotion/styled";
import { useState, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Text } from "theme-ui";
import languageConfig from "../../../../config/languages.json";
import { MobileAndUp, PhonesOnly } from "packages/dsaui/src/components/devices";
import { Select } from "packages/dsaui/src/components/forms";
import { Globe } from "packages/dsaui/src/components/icons";
import { themeGet } from "packages/dsaui/src/utils";
import routes from "routes";

interface WrapperProps {
  children: ReactNode;
}

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  border-top: 1px solid ${themeGet("colors.midGray")};
  background-color: ${themeGet("colors.background")};
  display: inline-flex;
  justify-content: space-between;
  font-weight: 400;
`;

const FooterGroup = styled.div<WrapperProps>`
  display: flex;
  align-items: center;
  padding: ${themeGet("space.3")};

  > * {
    margin-right: ${themeGet("space.3")} !important;

    > span {
      margin-right: ${themeGet("space.3")} !important;
    }
  }
`;

// TODO: Footer should be generic component, not tied to any other dependencies like the app routes or i18n
export const Footer = (): JSX.Element => {
  const { i18n, t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const year = new Date().getFullYear();

  const changeLanguage = (e: React.FormEvent<HTMLSelectElement>): void => {
    i18n.changeLanguage(e.currentTarget.value);
    setCurrentLanguage(e.currentTarget.value);
  };

  return (
    <Wrapper>
      <FooterGroup>
        <PhonesOnly>
          <Text as="span">
            <span role="img" aria-label="rose">
              🌹
            </span>
          </Text>
          <Text as="span">|</Text>
          <Link
            href={routes.external.support}
            target="_blank"
            sx={{ fontWeight: "body" }}
          >
            {t("footer.support")}
          </Link>
        </PhonesOnly>
        <MobileAndUp>
          <Text as="span">
            <span role="img" aria-label="rose">
              🌹
            </span>
          </Text>
          <Text as="span">{year}</Text>
          <Text as="span">|</Text>
          <Link
            href={routes.external.support}
            target="_blank"
            sx={{ fontWeight: "body" }}
          >
            {t("footer.support")}
          </Link>
        </MobileAndUp>
      </FooterGroup>
      {languageConfig.languageSelectionEnabled && (
        <FooterGroup>
          <Globe color={themeGet("colors.primary")} />
          <Select
            className="min-w-25"
            name="language-select"
            onChange={changeLanguage}
            value={currentLanguage}
          >
            <option value="en">{t("footer.languages.en")}</option>
            <option value="es">{t("footer.languages.es")}</option>
          </Select>
        </FooterGroup>
      )}
    </Wrapper>
  );
};

export default Footer;
